import React, { useState } from "react";
import { createPortal } from "react-dom";
import { QueryObserverResult, useQueries } from "@tanstack/react-query";
import { TransactionDetailsResponseDto } from "@common/dto/transactions/transaction-details-response.dto";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { transactionDetailsQueryOptions } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { MatchableItem } from "./types";
import { MultiTransactionAllocationContent } from "./MultiTransactionAllocationContent";
import {
    AllocationColumnSettings,
    AllocationDrawerShell,
} from "./editAllocation/AllocationDrawerLayout";
import { documentMatchItemType } from "./editAllocation/allocationHelpers";
import {
    CROSS_ENTITY_BILL_REQUIRES_ACCRUAL_LEDGER_REASON,
    getCrossEntityBillBlockedTransactionIds,
} from "./editAllocation/crossEntityBillGuard";
import { hasMatchesOutsideDocument } from "./editAllocation/multiAllocationHelpers";
import { TransactionLedgerContexts } from "./editAllocation/transactionLedgerContexts";
import { useMultiAllocationLedgerContext } from "./editAllocation/useMultiAllocationLedgerContext";
import { useAllocationView } from "./editAllocation/useAllocationView";
import { useSequentialAllocationSave } from "./editAllocation/useSequentialAllocationSave";

interface Props {
    open: boolean;
    onClose: () => void;
    documentId: string;
    documentItem: MatchableItem;
    transactionIds: number[];
    onCloseAfterSave: () => void;
}

function combineTransactionDetails(
    results: Array<QueryObserverResult<TransactionDetailsResponseDto>>,
) {
    return {
        transactions: results.flatMap((result) =>
            result.data ? [result.data.transaction] : [],
        ),
        isError: results.some((result) => result.isError && !result.data),
        isPending: results.some((result) => result.isPending),
    };
}

type ContentProps = React.ComponentProps<
    typeof MultiTransactionAllocationContent
>;

interface LoadedAllocationProps extends Omit<ContentProps, "viewState"> {
    headerSlot: HTMLDivElement | null;
}

const LoadedMultiTransactionAllocation: React.FC<LoadedAllocationProps> = ({
    headerSlot,
    ...contentProps
}) => {
    const { enabled: isGlFirst } = useIsGlFirst();
    const viewState = useAllocationView({
        isGlFirst,
        hasAccrualLedger: contentProps.ledgerContext.hasAnyAccrualLedger,
        matchItemType: contentProps.matchItemType,
    });
    return (
        <>
            {headerSlot &&
                createPortal(
                    <AllocationColumnSettings viewState={viewState} />,
                    headerSlot,
                )}
            <MultiTransactionAllocationContent
                {...contentProps}
                viewState={viewState}
            />
        </>
    );
};

interface BlockedState {
    header: string;
    body: string;
}

function getBlockedState({
    documentId,
    documentItem,
    transactions,
    contextsByTransactionId,
}: {
    documentId: string;
    documentItem: MatchableItem;
    transactions: Array<TransactionDetailsResponseDto["transaction"]>;
    contextsByTransactionId: TransactionLedgerContexts;
}): BlockedState | null {
    const hasOtherDocumentMatches = transactions.some((transaction) =>
        hasMatchesOutsideDocument({
            documentId,
            accrualMatches: transaction.accrualMatches ?? [],
        }),
    );
    if (hasOtherDocumentMatches) {
        return {
            header: "Allocate these transactions one at a time",
            body: "Some of them are also matched to other documents.",
        };
    }
    const hasBlockedCrossEntityBill =
        getCrossEntityBillBlockedTransactionIds({
            document: documentItem,
            transactions,
            contextsByTransactionId,
        }).length > 0;
    if (hasBlockedCrossEntityBill) {
        return {
            header: "Can't allocate these transactions to this bill",
            body: `${CROSS_ENTITY_BILL_REQUIRES_ACCRUAL_LEDGER_REASON}. Unmatch the transactions from entities without one.`,
        };
    }
    return null;
}

export const MultiTransactionAllocationDrawer: React.FC<Props> = ({
    open,
    onClose,
    documentId,
    documentItem,
    transactionIds,
    onCloseAfterSave,
}) => {
    const {
        transactions,
        isError: isTransactionsError,
        isPending,
    } = useQueries({
        queries: transactionIds.map(transactionDetailsQueryOptions),
        combine: combineTransactionDetails,
    });
    const ledgerContext = useMultiAllocationLedgerContext(transactions);
    const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);
    const { save, isSaving, hasCommittedChanges, scheduleResolutionModals } =
        useSequentialAllocationSave({ onAllSaved: onCloseAfterSave });

    const isError = isTransactionsError || ledgerContext.isError;
    const isLoaded = !isPending && !ledgerContext.isLoading;
    const blockedState = isLoaded
        ? getBlockedState({
              documentId,
              documentItem,
              transactions,
              contextsByTransactionId: ledgerContext.contextsByTransactionId,
          })
        : null;
    const isReady = !isError && isLoaded && !blockedState;

    const handleClose = () => {
        if (isSaving) {
            return;
        }
        if (hasCommittedChanges) {
            onCloseAfterSave();
        } else {
            onClose();
        }
    };

    return (
        <>
            <AllocationDrawerShell
                open={open}
                onClose={handleClose}
                headerAddon={<div ref={setHeaderSlot} />}
            >
                {isReady && (
                    <LoadedMultiTransactionAllocation
                        headerSlot={headerSlot}
                        documentItem={documentItem}
                        transactions={transactions}
                        ledgerContext={ledgerContext}
                        matchItemType={documentMatchItemType([documentItem])}
                        save={save}
                        isSaving={isSaving}
                        onClose={handleClose}
                    />
                )}
                {isError && (
                    <EmptyState header="Couldn't load the transactions" />
                )}
                {!isError && blockedState && (
                    <EmptyState
                        header={blockedState.header}
                        body={blockedState.body}
                    />
                )}
                {!isError && !blockedState && !isLoaded && <Loader />}
            </AllocationDrawerShell>
            {scheduleResolutionModals}
        </>
    );
};
